import * as React from "react"
import { getStudents, getGraduacoes, deleteStudent } from "../auth/api"
import EditProfileModal from "./EditProfileModal"
import ConfirmModal from "./ConfirmModal"
import StudentAttendanceModal from "./StudentAttendanceModal"
import Avatar from "./Avatar"

export default function StudentManagement({ onBack }) {
  const [students, setStudents] = React.useState([])
  const [graduacoes, setGraduacoes] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState("")
  const [search, setSearch] = React.useState("")
  const [sortBy, setSortBy] = React.useState("nome") // "nome" | "graduacao"
  const [graduacaoDir, setGraduacaoDir] = React.useState("desc") // "desc" | "asc"
  const [editingStudent, setEditingStudent] = React.useState(null)
  const [deletingStudent, setDeletingStudent] = React.useState(null)
  const [viewingAttendance, setViewingAttendance] = React.useState(null)

  React.useEffect(() => {
    let mounted = true
    async function load() {
      try {
        const [studentsList, graduacoesList] = await Promise.all([
          getStudents(),
          getGraduacoes(),
        ])
        if (mounted) {
          setStudents(studentsList)
          setGraduacoes(graduacoesList)
        }
      } catch (err) {
        if (mounted) setError(err?.message || "Erro ao carregar alunos.")
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  function handleStudentSaved(updatedProfile) {
    setStudents((prev) =>
      prev.map((s) => (s.id === updatedProfile.id ? updatedProfile : s)),
    )
    setEditingStudent(null)
  }

  async function handleConfirmDelete() {
    await deleteStudent(deletingStudent.id)
    setStudents((prev) => prev.filter((s) => s.id !== deletingStudent.id))
    setDeletingStudent(null)
  }

  const graduacaoRank = React.useMemo(() => {
    const map = {}
    graduacoes.forEach((g, idx) => {
      map[g] = idx
    })
    return map
  }, [graduacoes])

  const filteredAndSorted = React.useMemo(() => {
    const term = search.trim().toLowerCase()
    const filtered = term
      ? students.filter((s) =>
          (s.nomeCompleto || "").toLowerCase().includes(term),
        )
      : students

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === "graduacao") {
        const rankA = graduacaoRank[a.graduacao] ?? 999
        const rankB = graduacaoRank[b.graduacao] ?? 999
        const diff = graduacaoDir === "desc" ? rankB - rankA : rankA - rankB
        if (diff !== 0) return diff
      }
      return (a.nomeCompleto || "").localeCompare(b.nomeCompleto || "", "pt-BR")
    })

    return sorted
  }, [students, search, sortBy, graduacaoDir, graduacaoRank])

  return (
    <div className="w-full max-w-3xl bg-slate-50 backdrop-blur rounded-2xl p-4 sm:p-6 shadow-2xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-semibold break-words">
            Gerenciar alunos
          </h1>
          <p className="text-slate-950 mt-1">
            {students.length}{" "}
            {students.length === 1 ? "aluno cadastrado" : "alunos cadastrados"}
          </p>
        </div>
        <button
          onClick={onBack}
          className="shrink-0 self-start sm:self-auto text-sm px-4 py-2 rounded-lg border-2 border-cor-base text-cor-base font-semibold active:scale-[.94] active:duration-75 transition-all hover:scale-[1.05]"
          type="button">
          Voltar ao painel
        </button>
      </div>

      {/* Busca e ordenação */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pesquisar aluno por nome..."
          className="flex-1 border-2 border-slate-200 rounded-xl p-2 px-4 bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-cor-base transition-colors"
        />

        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setSortBy("nome")}
            className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
              sortBy === "nome"
                ? "bg-cor-base text-slate-100"
                : "border-2 border-cor-base text-cor-base hover:scale-[1.03]"
            }`}>
            Ordem alfabética
          </button>
          <button
            type="button"
            onClick={() => {
              if (sortBy !== "graduacao") {
                setSortBy("graduacao")
                setGraduacaoDir("desc")
              } else {
                setGraduacaoDir((prev) => (prev === "desc" ? "asc" : "desc"))
              }
            }}
            className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
              sortBy === "graduacao"
                ? "bg-cor-base text-slate-100"
                : "border-2 border-cor-base text-cor-base hover:scale-[1.03]"
            }`}>
            Ordem por graduação{" "}
            {sortBy === "graduacao" && (graduacaoDir === "desc" ? "↓" : "↑")}
          </button>
        </div>
      </div>

      {/* Lista */}
      <div className="mt-6 grid gap-3 max-h-[55vh] overflow-y-auto pr-1">
        {error ? (
          <div className="text-red-700">{error}</div>
        ) : loading ? (
          <div className="text-slate-950">Carregando alunos...</div>
        ) : filteredAndSorted.length === 0 ? (
          <div className="text-slate-500 text-center py-8">
            {search
              ? "Nenhum aluno encontrado com esse nome."
              : "Nenhum aluno cadastrado ainda."}
          </div>
        ) : (
          filteredAndSorted.map((student) => (
            <div
              key={student.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border-2 border-slate-100 rounded-xl p-4">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar
                  src={student.fotoPerfilUrl}
                  size="md"
                  alt={student.nomeCompleto}
                  expandable
                />
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 break-words">
                    {student.nomeCompleto}
                  </p>
                  <p className="text-sm text-slate-500 break-words">
                    {student.graduacao} · {student.email}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0 self-start sm:self-auto">
                <button
                  onClick={() => setViewingAttendance(student)}
                  type="button"
                  className="px-4 py-2 rounded-lg border-2 border-cor-base text-cor-base font-semibold text-sm active:scale-[.94] active:duration-75 transition-all hover:scale-[1.05]">
                  Presenças
                </button>
                <button
                  onClick={() => setEditingStudent(student)}
                  type="button"
                  className="px-4 py-2 rounded-lg bg-cor-base text-slate-100 font-semibold text-sm active:scale-[.94] active:duration-75 transition-all hover:scale-[1.05]">
                  Editar
                </button>
                <button
                  onClick={() => setDeletingStudent(student)}
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold text-sm active:scale-[.94] active:duration-75 transition-all hover:scale-[1.05]">
                  Excluir
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {viewingAttendance && (
        <StudentAttendanceModal
          student={viewingAttendance}
          onClose={() => setViewingAttendance(null)}
        />
      )}

      {editingStudent && (
        <EditProfileModal
          profile={editingStudent}
          studentId={editingStudent.id}
          onClose={() => setEditingStudent(null)}
          onSaved={handleStudentSaved}
        />
      )}

      {deletingStudent && (
        <ConfirmModal
          title="Excluir aluno"
          message={`Tem certeza que deseja excluir ${deletingStudent.nomeCompleto}? Essa ação não pode ser desfeita e também remove o histórico de presenças desse aluno.`}
          confirmLabel="Excluir"
          danger
          onConfirm={handleConfirmDelete}
          onClose={() => setDeletingStudent(null)}
        />
      )}
    </div>
  )
}
